import * as React from 'react';
import { InfiniteTable, InfiniteTableProps, Menu, MenuItem } from 'components';
import { formatPhoneNumber, formatPhoneNumberAsLink } from 'utils';

import { ContactListSortOrder } from '@bb/contacts';
import {
  AddressListingCell,
  LinkCell,
  TagsCell,
  NameCell,
  DropdownCell,
} from './TableCells';

const { useMemo } = React;

export interface ArchivedTableProps
  extends Omit<InfiniteTableProps, 'columns'> {
  onRestore: (contactId: string) => void;
  sort?: {
    field: string;
    order: ContactListSortOrder;
  };
  filter?: Record<string, string[]>;
}

const ArchivedTable: React.FC<ArchivedTableProps> = ({
  onRestore,
  sort,
  filter,
  ...props
}) => {
  const ARCHIVED_COLUMNS = useMemo(
    () => [
      {
        title: 'Name',
        dataIndex: 'firstName',
        width: 220,
        sorter: true,
        render: (text?: string, record?: any) => (
          <NameCell
            loading={!record}
            firstName={record?.firstName}
            lastName={record?.lastName}
          />
        ),
      },
      {
        title: 'type',
        dataIndex: 'tags',
        width: 150,
        sorter: true,
        sortOrder: sort?.field === 'tags' && sort.order,
        filteredValue: filter?.tags || null,
        filters: [
          {
            text: 'Buyer',
            value: 'buyer',
          },
          {
            text: 'Seller',
            value: 'seller',
          },
        ],
        render: (text?: string, record?: any) => (
          <TagsCell loading={!record} tags={record?.tags} />
        ),
      },
      {
        title: 'phone',
        dataIndex: 'phone',
        width: 125,
        render: (text?: string, record?: any) => (
          <LinkCell
            loading={!record}
            value={
              record?.phones?.[0]?.number &&
              formatPhoneNumber(record?.phones?.[0]?.number)
            }
            linktext={formatPhoneNumberAsLink(record?.phones?.[0]?.number)}
          />
        ),
      },
      {
        title: 'email',
        dataIndex: 'email',
        width: 170,
        render: (text?: string, record?: any) => (
          <LinkCell loading={!record} value={record?.email} type="email" />
        ),
      },
      {
        title: 'address / listing',
        dataIndex: 'listings',
        width: 380,
        render: (text?: string, record?: any) => (
          <AddressListingCell
            loading={!record}
            listings={record?.listings}
            address={record?.address || record?.companyAddress}
          />
        ),
      },
      {
        title: '',
        dataIndex: 'id',
        width: 200,
        render: (text?: string, record?: any) => {
          const menu = (
            <Menu>
              <MenuItem
                key={'restore'}
                onClick={e => {
                  onRestore(record.id);
                }}
              >
                Restore
              </MenuItem>
            </Menu>
          );

          return <DropdownCell dropdownRender={() => menu} />;
        },
      },
    ],
    [onRestore],
  );

  return <InfiniteTable {...props} columns={ARCHIVED_COLUMNS} />;
};

export default ArchivedTable;
